import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Loader2, MessagesSquare } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { darkPageCanvasBackgroundClassName } from "@/lib/page-canvas";
import { cn } from "@/lib/utils";
import { AddSourcesDialog } from "@/apps/rag/components/AddSourcesDialog.js";
import { ChatComposer, type AttachedDoc } from "@/apps/rag/components/ChatComposer.js";
import { ChatMessages } from "@/apps/rag/components/ChatMessages.js";
import { RagChatShell } from "@/apps/rag/components/RagChatShell.js";
import { CitationPanel } from "@/apps/rag/components/CitationPanel.js";
import {
  DocumentViewerPanel,
  type ActiveDocumentView,
} from "@/apps/rag/components/DocumentViewerPanel.js";
import { RagEmptyState } from "@/apps/rag/components/RagEmptyState.js";
import {
  getChatHistory,
  getThread,
  getThreadAttachmentProcessingStatus,
  listPublicLibraries,
  pollThreadAttachmentUntilDone,
  updateThreadLibraries,
  uploadThreadAttachment,
} from "@/apps/rag/api/rag-api.js";
import {
  mapApiProcessingStatus,
  mapChatMessage,
  mapPublicLibrary,
  mapThread,
} from "@/apps/rag/api/mappers.js";
import type { ApiProcessingStatus } from "@/apps/rag/api/types.js";
import { BACK_TO_GLOBAL_CHAT_LABEL } from "@/apps/rag/constants.js";
import { useRefreshRagNav } from "@/apps/rag/hooks/RagNavRefreshContext.js";
import { useAttachedDocPolling } from "@/apps/rag/hooks/useAttachedDocPolling.js";
import { useRagChatSubmit } from "@/apps/rag/hooks/useRagChatSubmit.js";
import { useAgenticPreference } from "@/apps/rag/hooks/useAgenticPreference.js";
import type { OnOpenCitationDocument } from "@/apps/rag/lib/open-citation-document.js";
import type { RagCitation, RagLibrary, RagMessage } from "@/apps/rag/types.js";
import { regulatoryGlobalChatsPath } from "../paths.js";

type LoadStatus = "loading" | "ready" | "error" | "not-found";

type RegulatoryGlobalChatLocationState = {
  initialMessage?: string;
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Failed to load chat";
}

function isNotFoundError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return message.includes("404") || message.includes("not found");
}

export function RegulatoryGlobalChatPage() {
  const { chatId = "" } = useParams();
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const refreshRagNav = useRefreshRagNav();
  const initialSendHandledRef = useRef<string | null>(null);
  const [libraries, setLibraries] = useState<RagLibrary[]>([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);
  const [messages, setMessages] = useState<RagMessage[]>([]);
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [activeCitation, setActiveCitation] = useState<RagCitation | null>(null);
  const [activeDocument, setActiveDocument] = useState<ActiveDocumentView | null>(null);

  const handleOpenDocument = useCallback<OnOpenCitationDocument>(
    (sourceId, { title, initialPage, sourceType, documentVersionId, highlightText }) => {
      setActiveDocument({
        ...(sourceType === "attachment"
          ? { attachmentId: sourceId, threadId: chatId }
          : { documentId: sourceId, documentVersionId }),
        title,
        initialPage,
        highlightText,
      });
    },
    [chatId],
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [attachedDocs, setAttachedDocs] = useState<AttachedDoc[]>([]);

  const pollAttachment = useCallback(
    (
      attachmentId: string,
      onStatus?: (status: ApiProcessingStatus | null | undefined) => void,
    ) => pollThreadAttachmentUntilDone(chatId, attachmentId, { onStatus }),
    [chatId],
  );

  const syncAttachmentStatus = useCallback(
    (attachmentId: string) => getThreadAttachmentProcessingStatus(chatId, attachmentId),
    [chatId],
  );

  const { startPolling: startPollingAttachment } = useAttachedDocPolling({
    items: attachedDocs,
    setItems: setAttachedDocs,
    enabled: loadStatus === "ready",
    poll: pollAttachment,
    syncStatus: syncAttachmentStatus,
  });

  const load = useCallback(async () => {
    setLoadStatus("loading");
    setError(null);
    try {
      const { libraries: libraryData } = await listPublicLibraries();
      const mappedLibraries = libraryData.map(mapPublicLibrary);
      setLibraries(mappedLibraries);

      try {
        const [{ thread }, history] = await Promise.all([
          getThread(chatId),
          getChatHistory(chatId),
        ]);

        const mappedThread = mapThread(thread);
        if (mappedThread.scope !== "public") {
          setLoadStatus("not-found");
          return;
        }

        setMessages(history.chats.map(mapChatMessage));
        const threadLibraryIds = mappedThread.libraryIds ?? [];
        setSelectedLibraryIds(
          threadLibraryIds.length > 0
            ? threadLibraryIds
            : mappedLibraries.map((library) => library.id),
        );
        setAttachedDocs(
          (thread.attachments ?? []).map((attachment) => ({
            id: attachment.id,
            name: attachment.title,
            status: mapApiProcessingStatus(attachment.processingStatus),
          })),
        );
        setLoadStatus("ready");
      } catch (threadError) {
        if (isNotFoundError(threadError)) {
          setLoadStatus("not-found");
          return;
        }
        setError(getErrorMessage(threadError));
        setLoadStatus("error");
      }
    } catch (loadError) {
      setError(getErrorMessage(loadError));
      setLoadStatus("error");
    }
  }, [chatId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedLibraries = useMemo(
    () => libraries.filter((lib) => selectedLibraryIds.includes(lib.id)),
    [libraries, selectedLibraryIds],
  );

  const scopeLabel =
    selectedLibraries.length > 0 && selectedLibraries.length >= libraries.length
      ? `All ${libraries.length} public ${libraries.length === 1 ? "library" : "libraries"}`
      : `${selectedLibraries.length} ${selectedLibraries.length === 1 ? "library" : "libraries"} selected`;

  async function handleLibraryIdsChange(nextIds: string[]) {
    if (nextIds.length === 0) return;

    const previousIds = selectedLibraryIds;
    setSelectedLibraryIds(nextIds);
    try {
      await updateThreadLibraries(chatId, nextIds);
    } catch {
      setSelectedLibraryIds(previousIds);
    }
  }

  const agenticPreference = useAgenticPreference();
  const { handleSubmit, handleRegenerate, isSubmitting, statusSteps } = useRagChatSubmit({
    chatId,
    messages,
    setMessages,
    onFirstMessageTitle: () => {
      refreshRagNav();
    },
    retrievalMode: agenticPreference.retrievalMode,
  });

  useEffect(() => {
    initialSendHandledRef.current = null;
  }, [chatId]);

  useEffect(() => {
    if (loadStatus !== "ready" || messages.length > 0) return;
    if (initialSendHandledRef.current === chatId) return;

    const initialMessage = (locationState as RegulatoryGlobalChatLocationState | null)?.initialMessage?.trim();
    if (!initialMessage) return;

    initialSendHandledRef.current = chatId;
    navigate(pathname, { replace: true, state: null });
    void handleSubmit(initialMessage);
  }, [
    chatId,
    handleSubmit,
    loadStatus,
    locationState,
    messages.length,
    navigate,
    pathname,
  ]);

  async function handleUpload(file: File, title?: string) {
    const { attachment } = await uploadThreadAttachment(chatId, file, title);
    const status = mapApiProcessingStatus(attachment.processingStatus);

    setAttachedDocs((prev) => [
      ...prev,
      { id: attachment.id, name: attachment.title, status },
    ]);

    startPollingAttachment(attachment.id, status);
  }

  if (loadStatus === "loading") {
    return (
      <section className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (loadStatus === "not-found") {
    return (
      <section className="flex flex-1 flex-col p-6">
        <RagEmptyState
          icon={MessagesSquare}
          title="Chat not found"
          description="This conversation may have been deleted or the link is outdated."
          action={
            <Button variant="outline" asChild>
              <Link to={regulatoryGlobalChatsPath()}>{BACK_TO_GLOBAL_CHAT_LABEL}</Link>
            </Button>
          }
        />
      </section>
    );
  }

  if (loadStatus === "error") {
    return (
      <section className="flex flex-1 flex-col p-6">
        <RagEmptyState
          icon={AlertCircle}
          title="Couldn't load chat"
          description={
            error ??
            "The regulatory service may be unavailable. Check that mastra-rag-api is running."
          }
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" onClick={() => void load()}>
                Retry
              </Button>
              <Button variant="outline" asChild>
                <Link to={regulatoryGlobalChatsPath()}>{BACK_TO_GLOBAL_CHAT_LABEL}</Link>
              </Button>
            </div>
          }
        />
      </section>
    );
  }

  const pendingInitialMessage = Boolean(
    (locationState as RegulatoryGlobalChatLocationState | null)?.initialMessage?.trim(),
  );
  const showStartScreen = messages.length === 0 && !isSubmitting && !pendingInitialMessage;

  const composerScope = (
    <ChatComposer
      placeholder="Ask across selected public libraries…"
      scopeVariant="workspace"
      showLibraryScopeBar
      libraryScope={libraries.map((library) => ({
        id: library.id,
        name: library.name,
        selected: selectedLibraryIds.includes(library.id),
      }))}
      onLibraryIdsChange={(ids) => void handleLibraryIdsChange(ids)}
      scopeLabel={scopeLabel}
      attachedDocs={attachedDocs}
      onRemoveDoc={(docId) =>
        setAttachedDocs((prev) => prev.filter((doc) => doc.id !== docId))
      }
      onAttachDocs={() => setUploadOpen(true)}
      onSubmit={handleSubmit}
      disabled={isSubmitting}
      agenticAvailable={agenticPreference.available}
      agenticEnabled={agenticPreference.agentic}
      onAgenticChange={agenticPreference.setAgentic}
      className={
        showStartScreen
          ? "border-0 bg-transparent p-0 backdrop-blur-none dark:border-0 dark:bg-transparent"
          : undefined
      }
    />
  );

  if (showStartScreen) {
    const libraryCountLabel =
      libraries.length === 1 ? "1 library" : `${libraries.length} libraries`;

    return (
      <>
        <section
          className={cn(
            "relative flex min-h-[calc(100dvh-3rem)] flex-1 flex-col items-center justify-center overflow-hidden px-4 py-8 sm:px-6",
            darkPageCanvasBackgroundClassName,
          )}
        >
          <div className="relative z-10 flex w-full max-w-3xl flex-col items-center gap-8 text-center">
            <div className="space-y-2">
              <h1 className="text-2xl font-normal tracking-tight text-[var(--lm-foreground-strong)] sm:text-3xl dark:text-white">
                Where should we start?
              </h1>
              <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
                Regulatory · {libraryCountLabel}
              </p>
            </div>
            <div className="w-full">{composerScope}</div>
          </div>
        </section>
        <AddSourcesDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onUpload={handleUpload}
        />
      </>
    );
  }

  return (
    <section
      className="flex h-[calc(100dvh-3rem)] min-h-0 w-full max-w-full flex-1 gap-0 overflow-hidden p-0"
    >
      <RagChatShell className="rounded-none border-0 shadow-none dark:shadow-none">
        <ChatMessages
          messages={messages}
          activeCitationId={activeCitation?.id}
          onCitationClick={(citation) => {
            setActiveDocument(null);
            setActiveCitation(citation);
          }}
          threadId={chatId}
          onOpenDocument={handleOpenDocument}
          isThinking={
            isSubmitting &&
            (messages.length === 0 || messages[messages.length - 1]?.role === "user")
          }
          thinkingSteps={statusSteps}
          busy={isSubmitting}
          onRegenerate={() => void handleRegenerate()}
        />
        {composerScope}
      </RagChatShell>
      {activeDocument ? (
        <DocumentViewerPanel
          document={activeDocument}
          onClose={() => setActiveDocument(null)}
        />
      ) : activeCitation ? (
        <CitationPanel
          citation={activeCitation}
          threadId={chatId}
          onClose={() => setActiveCitation(null)}
          onOpenDocument={handleOpenDocument}
        />
      ) : null}
      <AddSourcesDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUpload}
      />
    </section>
  );
}
